import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Home, Users, GraduationCap, Building, ChevronLeft,
  Check, AlertTriangle, Settings, ArrowRight
} from 'lucide-react';
import { HMOManagement } from '../components/HMOManagement';
import { StudentLetManagement } from '../components/StudentLetManagement';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

// Static color class maps for Tailwind compilation
const bgColorMap = {
  blue: 'bg-blue-100',
  purple: 'bg-purple-100',
  green: 'bg-green-100',
  orange: 'bg-orange-100',
};

const textColorMap = {
  blue: 'text-blue-600',
  purple: 'text-purple-600',
  green: 'text-green-600',
  orange: 'text-orange-600',
};

const PROPERTY_TYPES = [
  {
    value: 'standard',
    label: 'Standard Let',
    description: 'Single household tenancy',
    icon: Home,
    color: 'blue'
  },
  {
    value: 'hmo',
    label: 'HMO',
    description: 'House in Multiple Occupation',
    icon: Users,
    color: 'purple'
  },
  {
    value: 'student_let',
    label: 'Student Let',
    description: 'Shared house for students',
    icon: GraduationCap,
    color: 'green'
  },
  {
    value: 'pbsa',
    label: 'PBSA / Student Halls',
    description: 'Purpose-Built Student Accommodation',
    icon: Building,
    color: 'orange'
  }
];

export default function PropertyTypeSettings() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [selectedType, setSelectedType] = useState('standard');
  const [activeTab, setActiveTab] = useState('type');

  useEffect(() => {
    loadProperty();
  }, [propertyId]);

  const loadProperty = async () => {
    try {
      const response = await fetch(`${API_URL}/api/properties/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProperty(data);
        setSelectedType(data.property_type || 'standard');
      }
    } catch (error) {
      console.error('Failed to load property:', error);
      toast.error('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  const updatePropertyType = async (newType) => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/property-types/properties/${propertyId}/type`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ property_type: newType })
        }
      );

      if (response.ok) {
        setSelectedType(newType);
        toast.success('Property type updated');
        
        // Switch to appropriate tab based on type
        if (newType === 'hmo') {
          setActiveTab('hmo');
        } else if (newType === 'student_let') {
          setActiveTab('student');
        } else {
          setActiveTab('type');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update property type');
      }
    } catch (error) {
      toast.error('Failed to update property type');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const currentTypeInfo = PROPERTY_TYPES.find(t => t.value === selectedType);
  const isSharedOccupancy = ['hmo', 'student_let', 'pbsa'].includes(selectedType);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto pb-20">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/landlord/properties/${propertyId}`)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Property
          </button>
          
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${bgColorMap[currentTypeInfo?.color] || 'bg-blue-100'}`}>
              {currentTypeInfo && <currentTypeInfo.icon className={`w-6 h-6 ${textColorMap[currentTypeInfo?.color] || 'text-blue-600'}`} />}
            </div>
            <div>
              <h1 className="text-2xl font-bold">Property Type Settings</h1>
              <p className="text-slate-600">
                {property?.address?.line1}, {property?.address?.city}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('type')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeTab === 'type' 
                ? 'bg-primary text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Property Type
          </button>
          {selectedType === 'hmo' && (
            <button
              onClick={() => setActiveTab('hmo')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === 'hmo' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              HMO Management
            </button>
          )}
          {selectedType === 'student_let' && (
            <button
              onClick={() => setActiveTab('student')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === 'student' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Student Let Settings
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'type' && (
          <div className="space-y-4">
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-bold mb-4">Select Property Type</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PROPERTY_TYPES.map(type => {
                  const Icon = type.icon;
                  const isSelected = type.value === selectedType;
                  
                  return (
                    <button
                      key={type.value}
                      onClick={() => updatePropertyType(type.value)}
                      disabled={saving}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      } ${saving ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${bgColorMap[type.color] || 'bg-blue-100'}`}>
                          <Icon className={`w-5 h-5 ${textColorMap[type.color] || 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{type.label}</span>
                            {isSelected && <Check className="w-5 h-5 text-primary" />}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Quick Info */}
            {isSharedOccupancy && (
              <Card className="p-4 sm:p-6 border-purple-200 bg-purple-50">
                <h3 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Shared Occupancy Features
                </h3>
                <ul className="text-sm text-purple-800 space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> Room-by-room inventory management
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> Multiple tenant tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> Communal area maintenance tracking
                  </li>
                  {selectedType === 'hmo' && (
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4" /> HMO licensing compliance
                    </li>
                  )}
                  {selectedType === 'student_let' && (
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4" /> Guarantor workflow
                    </li>
                  )}
                </ul>
                
                <Button 
                  className="mt-4" 
                  onClick={() => setActiveTab(selectedType === 'hmo' ? 'hmo' : 'student')}
                >
                  Configure {selectedType === 'hmo' ? 'HMO' : 'Student Let'} Settings
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'hmo' && selectedType === 'hmo' && (
          <HMOManagement 
            propertyId={propertyId} 
            jurisdiction={property?.jurisdiction}
          />
        )}

        {activeTab === 'student' && selectedType === 'student_let' && (
          <StudentLetManagement 
            propertyId={propertyId}
            jurisdiction={property?.jurisdiction}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
